import { useContext } from 'react';
import { useQuery } from '@apollo/client';
import {
  AssociatedActivitiesQuery,
  AssociatedActivitiesQueryVariables,
  AssociatedActivitiesQuery_node_TEDBOAktivitet__Associations_AktBokRubrik_All,
  AssociatedActivitiesQuery_node_TEDBOAktivitet__Associations_KnutnaAktiviteter_All,
} from '../Queries/AssociatedActivitiesQuery/_QueryTypes/AssociatedActivitiesQuery';
import associatedActivities from '../Queries/AssociatedActivitiesQuery/AssociatedActivitiesQuery.gql';
import KalenderTooltipComponent, {
  IKalenderLinkTooltipInfo,
  IKalenderResourceTooltipInfo,
} from './KalenderTooltipComponent';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { AppointmentTooltipContext } from './AppointmentTooltipContext';
import { IBookingType } from './SchedulerComponent/Components/AppointmentComponent';

const KalenderTooltipHandlerComponent = (appointmentData: IBookingType) => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const appointmentTooltipContext = useContext(AppointmentTooltipContext);

  const { data, loading } = useQuery<
    AssociatedActivitiesQuery,
    AssociatedActivitiesQueryVariables
  >(associatedActivities, {
    variables: {
      id: appointmentData.activityId,
    },
    skip:
      appointmentData.aktivityType === 'OUTLOOK' ||
      !activeDatabaseContext.activeDatabase ||
      appointmentData.activityId == null,
    onCompleted: () => {
      appointmentTooltipContext.actions?.current?.updatePosition();
    },
  });

  const filterResources = (
    resources:
      | AssociatedActivitiesQuery_node_TEDBOAktivitet__Associations_AktBokRubrik_All[]
      | null
      | undefined,
    filterCallback: (baseType: string | null | undefined) => boolean
  ): IKalenderResourceTooltipInfo[] | undefined =>
    resources?.reduce(
      (filteredResources: IKalenderResourceTooltipInfo[], resource) => {
        const baseType =
          resource._Associations?.Resurs?._Associations?.ResRestyp.Bastyp ??
          resource._Associations?.AktResRubrik?._Associations?.Resrubrik
            ?._Associations?.TillatResTyp?.Bastyp;

        if (filterCallback(baseType)) {
          filteredResources.push({
            stallTidFran: resource.FRANTID,
            stallTidTill: resource.TILLTID,
            resourceTypeName:
              resource._Associations?.Resurs?._Associations?.ResRestyp
                .BO_RESURSTYP_NAMN,
            resourceName: resource.RESURS_NAMN,
            prio_ordning: resource._Associations?.Resurs?.PRIO_ORDNING,
            aktResRub_PosNr: resource.AKTRESRUB_POSNR,
            resourceTitle: resource.Resursrubrik,
            bo_resurs_id: resource?._Associations?.Resurs?.BO_RESURS_ID,
          });
        }

        return filteredResources;
      },
      []
    );

  const getLinkResources = (
    links:
      | AssociatedActivitiesQuery_node_TEDBOAktivitet__Associations_KnutnaAktiviteter_All[]
      | null
      | undefined
  ): IKalenderLinkTooltipInfo[] | undefined =>
    links?.map(link => ({
      linkTidFran: link.FRANTID,
      linkTidTill: link.TILLTID,
      linkName: link.TITEL ?? undefined,
      linkLocation: link._Associations?.Resurs1?.RESURS_NAMN ?? undefined,
      linkInfo: link.TIDSTEXT ?? undefined,
    }));

  if (!loading && data?.node && data.node.__typename === 'TEDBOAktivitet') {
    return (
      <KalenderTooltipComponent
        activityId={appointmentData.activityId}
        bookingTimeStart={data.node.FRANTID}
        bookingTimeEnd={data.node.TILLTID}
        locations={filterResources(
          data.node._Associations?.AktBokRubrik_All,
          baseType => baseType === 'Plats'
        )}
        persons={filterResources(
          data.node._Associations?.AktBokRubrik_All,
          baseType => baseType === 'Person'
        )}
        otherResources={filterResources(
          data.node._Associations?.AktBokRubrik_All,
          baseType => baseType !== 'Plats' && baseType !== 'Person'
        )}
        externalNotes={data.node.BESKRIVNING ?? undefined}
        internalNotes={data.node.NOTERING2 ?? undefined}
        links={getLinkResources(data.node._Associations?.KnutnaAktiviteter_All)}
        bookinSignature={data.node.BOKNINGSSIGNATUR}
        repCount={data.node.AntalRepeteradeAktiviteter}
        bokadAv={data.node.BokadAv}
        bevakning={data.node.BEVAKNING}
        bevakningtText={data.node.BEVAKNING_TEXT}
        externPublicering={data.node.SKK_IS_PUBLISHED}
        begravningsordning={data.node._Associations?.Begravningstyp?.BENAMNING}
        bookingType={data.node._typ}
      />
    );
  }

  return (
    <KalenderTooltipComponent
      activityId={appointmentData.activityId}
      bookingTimeStart={appointmentData.aktStartDate}
      bookingTimeEnd={appointmentData.aktEndDate}
      persons={
        appointmentData.isPerson
          ? [
              {
                resourceName: appointmentData.resourceName,
                stallTidFran: appointmentData.aktStartDate,
                stallTidTill: appointmentData.aktEndDate,
              },
            ]
          : []
      }
      locations={
        appointmentData.isPerson
          ? [
              {
                resourceName: appointmentData.location?.resName,
                stallTidFran: appointmentData.aktStartDate,
                stallTidTill: appointmentData.aktEndDate,
              },
            ]
          : [
              {
                resourceName: appointmentData.resourceName,
                stallTidFran: appointmentData.aktStartDate,
                stallTidTill: appointmentData.aktEndDate,
              },
              {
                resourceName: appointmentData.location?.resName,
                stallTidFran: appointmentData.aktStartDate,
                stallTidTill: appointmentData.aktEndDate,
              },
            ]
      }
    />
  );
};

export default KalenderTooltipHandlerComponent;
